import {
  faCheck,
  faCircleCheck,
  faCross,
  faTriangleExclamation,
  faXmark
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, useEffect, useState } from "react";
import spreadsheetIcon from "../../../../../../assets/images/spreadsheetToasterIcon.svg";
import "./upload-progress-toaster.component.scss";

export type UploadPhase =
  | "idle"
  | "uploading"
  | "processing"
  | "complete"
  | "error"
  | "failed";

export interface UploadSummary {
  added: number;
  total: number;
  errors: number;
  message: string;
  hasValidationErrors: boolean;
  resultStatus?: boolean;
}

interface UploadProgressToasterProps {
  phase: UploadPhase;
  fileName: string;
  progress: number;
  summary: UploadSummary | null;
  onViewSummary: () => void;
  onClose: () => void;
  onSeeDetails?: () => void;
}

const UploadProgressToaster: FC<UploadProgressToasterProps> = ({
  phase,
  fileName,
  progress,
  summary,
  onViewSummary,
  onClose,
  onSeeDetails,
}) => {
  const isInProgress = phase === "uploading" || phase === "processing";
  const [hasAutoTriggeredViewSummary, setHasAutoTriggeredViewSummary] = useState(false);

  useEffect(() => {
    if (
      phase === "complete" &&
      summary?.hasValidationErrors &&
      !hasAutoTriggeredViewSummary
    ) {
      onViewSummary();
      setHasAutoTriggeredViewSummary(true);
    }
  }, [phase, summary, onViewSummary, hasAutoTriggeredViewSummary]);

  useEffect(() => {
    if (phase === "uploading") {
      setHasAutoTriggeredViewSummary(false);
    }
  }, [phase]);

  if (phase === "idle") return null;

  const getTitle = () => {
    switch (phase) {
      case "uploading":
        return "Uploading 1 item";
      case "processing":
        return "Processing...";
      case "complete":
        return summary?.hasValidationErrors
          ? "Upload Complete — Review Needed"
          : "Upload Complete";
      case "error":
        return "Upload Error";
      case "failed":
        return "Upload Failed";
      default:
        return "";
    }
  };

  const radius = 17;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (progress / 100) * circumference;

  return (
    <>
      {/* Backdrop */}
      <div className="upload-toaster__backdrop" />

      {/* Toaster card */}
      <div className="upload-toaster">
        {/* Header */}
        <div className="upload-toaster__header">
          <span className="upload-toaster__title">{getTitle()}</span>
          {(phase === "complete" || phase === "failed" || phase === "error") && (
            <button
              type="button"
              onClick={() => { onClose() }}
              className="upload-toaster__btn"
              title={"Close"}
            >
              <FontAwesomeIcon
                icon={faXmark}
                className="upload-toaster__close-icon"
              />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="upload-toaster__body">
          <div className="upload-toaster__content">
            {/* File icon */}
            <div className="upload-toaster__file-icon">
              <img
                src={spreadsheetIcon}
                alt="Spreadsheet"
                width="22"
                height="22"
              />
            </div>

            {/* Middle content */}
            <div className="upload-toaster__middle">
              <div className="upload-toaster__filename">{fileName}</div>

              {phase === "uploading" && (
                <>
                  <div className="upload-toaster__sub-text">
                    Uploading... {progress}%
                  </div>
                  <div className="upload-toaster__progress-track">
                    <div
                      className="upload-toaster__progress-fill"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </>
              )}

              {phase === "processing" && (
                <>
                  <div className="upload-toaster__sub-text">
                    Processing file... {progress}%
                  </div>
                  <div className="upload-toaster__progress-track">
                    <div
                      className="upload-toaster__progress-fill"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </>
              )}

              {phase === "complete" || phase === "failed" && summary && (
                <>
                  {!summary?.hasValidationErrors && (
                    <div className="upload-toaster__success-text">
                      {summary.message}
                    </div>
                  )}
                </>
              )}

              {phase === "complete" && onSeeDetails && (
                <button
                  type="button"
                  onClick={onSeeDetails}
                  className="btn btn-link p-0 mt-1"
                  style={{ fontSize: "13px" }}
                >
                  See details
                </button>
              )}

              {phase === "error" && (
                <div className="upload-toaster__error-text">
                  Upload failed. Please try again.
                </div>
              )}
            </div>

            {/* Right-side indicator */}
            {isInProgress && (
              <div className="upload-toaster__indicator">
                <svg width="44" height="44" viewBox="0 0 44 44">
                  <circle
                    cx="22"
                    cy="22"
                    r={radius}
                    fill="none"
                    stroke="#e0e0e0"
                    strokeWidth="4"
                  />
                  <circle
                    cx="22"
                    cy="22"
                    r={radius}
                    fill="none"
                    stroke="#1976D2"
                    strokeWidth="4"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    strokeLinecap="round"
                    transform="rotate(-90 22 22)"
                    className="upload-toaster__progress-arc"
                  />
                  <text
                    x="22"
                    y="26"
                    textAnchor="middle"
                    fill="#1976D2"
                    fontSize="10"
                    fontWeight="bold"
                  >
                    {progress}%
                  </text>
                </svg>
              </div>
            )}

            {phase === "complete" && summary?.hasValidationErrors && (
              <div className="upload-toaster__warning-circle">
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  className="upload-toaster__warning-icon"
                />
              </div>
            )}

            {phase === "complete" &&
              summary &&
              summary?.resultStatus !== undefined &&
              (summary?.resultStatus ? (
                <div className="upload-toaster__indicator">
                  <FontAwesomeIcon
                    icon={faCircleCheck}
                    className="upload-toaster__success-icon"
                  />
                </div>
              ) : (
                <div className="upload-toaster__warning-circle">
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="upload-toaster__warning-icon"
                  />
                </div>
              ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default UploadProgressToaster;